// src/i18n.js
import { createI18n } from 'vue-i18n';

const messages = {
  es: {
    nav: {
      home: "Inicio",
      about: "Nosotros",
      dashboard: "Simulación",     // También puede ser "Panel" o "Dashboard"
      skills: "Producto",       // Cambia a "Habilidades" si ese es el enfoque real de la sección
      pricing: "Precios",  // o "Suscripción"
      faq: "FAQ",
      contact: "Beta"
    },

    hero: {
      badge: 'Tu copiloto financiero',
      title: 'Ahorro, inversión, deudas y gastos en equilibrio.',
      subtitle: 'Organiza tu dinero en bolsillos inteligentes y toma mejores decisiones, sin esfuerzo.',
      cta: 'PROBAR BETA',
      demo: 'Ver demo',
      cards: {
        analytics: 'Analítica financiera',
        savings: 'Ahorro inteligente',
        investment: 'Inversión simple'
      },
      scroll: 'Desplázate para saber más',
      stats: {
        users: 'Usuarios',
        satisfaction: 'Satisfacción',
        managed: 'Dinero gestionado'
      }
    },

    about: {
      title: 'Nosotros',
      subtitle: 'El copiloto financiero de LATAM',
      byline: 'Invextia · Bogotá, Colombia',
      body1:
        'En <strong>Invextia</strong> estamos construyendo la nueva forma de manejar las finanzas personales en Latinoamérica. Nuestro <strong>copiloto financiero impulsado por IA</strong> analiza automáticamente tus ingresos, gastos, deudas y metas para <em>distribuir tu dinero de la manera más inteligente</em>.',
      body2:
        'Funciona en tres pasos sencillos: conectas tus cuentas, defines tus objetivos, y nuestro motor de inteligencia artificial <strong>crea y ejecuta un plan de inversión, ahorro y pago de deudas</strong> con explicaciones claras y seguimiento en tiempo real.'
    },

    dashboard: {
      title: 'Simula tu Distribución Financiera',
      subtitle: 'Descubre cómo Invextia optimizaría tu dinero',
      form_title: 'Personaliza tu plan',
      income_label: 'Ingresos mensuales',
      income_placeholder: '3,500,000',
      annual: 'Anual',
      goal_label: 'Meta financiera',
      goal_placeholder: 'Selecciona tu objetivo',
      risk_label: 'Perfil de riesgo',
      risk_placeholder: 'Selecciona tu perfil',
      total_monthly: 'Total mensual',
      a11y_chart_label: 'Gráfico de pastel de la distribución financiera',
      goals: {
        retirement: 'Jubilación',
        house: 'Comprar casa',
        education: 'Educación',
        business: 'Negocio propio',
        travel: 'Viajes',
        emergency: 'Fondo de emergencia',
        wealth: 'Construcción de riqueza'
      },
      risk_profiles: {
        conservative: 'Conservador',
        moderate: 'Moderado',
        aggressive: 'Agresivo'
      },
      categories: {
        emergency: 'Fondo de emergencia',
        savings: 'Ahorros',
        expenses: 'Gastos',
        debts: 'Deudas',
        investments: 'Inversiones'
      },
      descriptions: {
        emergency: 'Protección ante imprevistos',
        savings: 'Alcanza tus metas futuras',
        expenses: 'Gastos del día a día',
        debts: 'Reduce tu carga financiera',
        investments: 'Haz crecer tu patrimonio'
      },
      distribution: 'Distribución recomendada',
      insights: 'Recomendaciones personalizadas',
      form_description: 'Completa tu información financiera',
      total_distribution: 'Distribución total',
      active_categories: 'Categorías activas',
      reset_button: 'Resetear',
      risk_descriptions: {
        conservative: 'Bajo riesgo',
        moderate: 'Riesgo balanceado',
        aggressive: 'Alto rendimiento'
      },
      insights_list: {
        raise_emergency: '⚠️ Considera aumentar tu fondo de emergencia para mayor seguridad.',
        diversify: '📈 Excelente enfoque en inversiones — diversifica para reducir riesgos.',
        pay_high_interest: '💳 Prioriza el pago de deudas con intereses altos.',
        set_specific_goals: '🎯 Gran disciplina de ahorro — define objetivos específicos.'
      },
      tips: {
        t1: 'Automatiza tus ahorros para alcanzar tus metas más rápido.',
        t2: 'Revisa tu distribución cada 3 meses y ajústala según tus necesidades.',
        t3: 'Un fondo de emergencia debe cubrir 3–6 meses de gastos.',
        t4: 'Invierte en tu educación financiera para mejores decisiones.'
      },
      // Estos textos de CTA se mantienen por si otro componente los usa.
      cta_text: 'Si quieres obtener un plan de distribución más detallado prueba nuestra versión beta hoy',
      cta_button: 'Probar Beta Gratis'
    },

    product: {
      title: 'Producto',
      how: 'Cómo funciona',
      steps: [
        'Conecta tus cuentas (Belvo)',
        'Analizamos ingresos y gastos',
        'Definimos metas y perfil de riesgo',
        'Plan de distribución automático',
        'Ejecución de inversión (Alpaca)',
        'Aprendizaje de tus ajustes'
      ],
      stackTitle: 'Stack',
      stack: [
        'Backend: FastAPI (Python)',
        'Frontend: Flutter & Web',
        'IA: LangChain + OpenAI + RAG',
        'Datos financieros: Belvo',
        'Broker: Alpaca',
        'DB: PostgreSQL (AWS)'
      ]
    },

    cases: { title: 'Preguntas Frecuentes', details: 'Ver detalles' },

    contact: {
      title: 'Únete a la lista de espera',
      early: 'Acceso anticipado',
      early_desc: 'Sé de los primeros en probar Invextia y recibir actualizaciones del lanzamiento.',
      trans: 'Transparencia',
      trans_desc: 'Solo te escribiremos con novedades importantes. Puedes darte de baja en cualquier momento.',
      name: 'Nombre',
      email: 'Correo',
      consent: 'Acepto recibir correos sobre el lanzamiento y la beta.',
      submit: 'Unirme a la lista',
      submitting: 'Uniéndote…',
      success: '¡Listo! Te avisaremos cuando abramos la beta.',
      error: 'No se pudo enviar. Inténtalo de nuevo.'
    },

    faq: {
      title: 'Preguntas Frecuentes',
      subtitle: 'Todo lo que necesitas saber sobre Invextia',
      items: [
        { q: '¿Qué es Invextia?', a: 'Invextia es una app fintech que te permite organizar tu dinero en bolsillos inteligentes, ahorrar para tus metas, invertir de forma simple y llevar el control de tus finanzas en un solo lugar.' },
        { q: '¿Es seguro usar Invextia?', a: 'Sí. La seguridad es nuestra prioridad. Utilizamos encriptación de nivel bancario y protocolos de protección avanzados para garantizar que tu información y tu dinero estén siempre seguros.' },
        { q: '¿Cómo funcionan los "bolsillos" de dinero?', a: 'Puedes separar tu dinero en diferentes bolsillos digitales según tus metas: ahorro, inversión, gastos o cualquier categoría que definas. Así tendrás claridad y control total.' },
        { q: '¿Invextia es un banco?', a: 'No somos un banco. Invextia es una plataforma tecnológica que te ayuda a organizar y gestionar tu dinero de forma inteligente, en alianza con instituciones reguladas.' },
        { q: '¿Qué necesito para registrarme?', a: 'Solo tu correo electrónico y un documento de identidad válido. El proceso es rápido y 100% digital.' },
        { q: '¿Dónde está disponible Invextia?', a: 'Actualmente estamos enfocados en Latinoamérica, empezando por Colombia. Pronto llegaremos a más países.' },
        { q: '¿Tiene algún costo usar Invextia?', a: 'Las funciones básicas son gratis. Algunos servicios premium, como opciones de inversión avanzada, podrán tener un costo adicional.' },
        { q: '¿Cómo puedo unirme a la lista de espera (waitlist)?', a: 'Haz clic en "Únete a la Waitlist" y deja tu correo. Te notificaremos tan pronto como Invextia esté disponible para ti.' }
      ]
    },

    pricing: {
      badge: 'Planes y precios',
      title: 'Elige el plan ideal para ti',
      subtitle: 'Comienza gratis y escala a más funciones cuando las necesites.',
      monthly: 'Mensual',
      annual: 'Anual',
      popular: 'Más popular',
      month: 'mes',
      year: 'año',
      save: 'Ahorra',
      upto: 'Hasta',
      maxReturn: 'Devolución máxima',
      included: 'Incluye',
      exclusive: 'Exclusivo',
      ctaPrimary: 'Próximamente',
      ctaSecondary: 'Próximamente',
      guarantee:
        'Sin permanencia. Cancela cuando quieras. Tu información está protegida con cifrado de nivel bancario.',
      tiers: {
        basic: {
          name: 'Básico',
          tagline: 'Para empezar a organizar tu dinero',
          features: {
            f1: 'Motor de distribución automática (bolsillos inteligentes).',
            f2: 'Recargas a tus bolsillos.',
            f3: 'Cashback 4% sobre recargas, hasta 200.000 COP → máximo 8.000 COP devueltos al mes.',
            f4: 'Acceso a reportes básicos de tus gastos.'
          }
        },
        advanced: {
          name: 'Avanzado',
          tagline: 'Más control y recomendaciones con IA',
          features: {
            f1: 'Todo lo del Básico.',
            f2: 'Chatbot con IA para sugerencias financieras simples (ahorro, control de gastos).',
            f3: 'Reportes detallados de hábitos de gasto y ahorro.',
            f4: 'Cashback 6% sobre recargas, hasta 500.000 COP → máximo 30.000 COP devueltos al mes.'
          },
          exclusive: {
            e1: 'Recomendaciones de ahorro personalizadas.'
          }
        },
        premium: {
          name: 'Premium',
          tagline: 'La experiencia completa de Invextia',
          features: {
            f1: 'Todo lo del Avanzado.',
            f2: 'Chatbot con IA avanzado (proyecciones, metas de inversión, asesoría personalizada).',
            f3: 'Acceso anticipado a nuevas funciones (integraciones bancarias, inversiones globales vía Alpaca).',
            f4: 'Cashback 8% sobre recargas, hasta 1.000.000 COP → máximo 80.000 COP devueltos al mes.'
          },
          exclusive: {
            e1: 'Invitaciones premium para amigos.',
            e2: 'Soporte prioritario.',
            e3: 'Beneficios exclusivos y promociones especiales.'
          }
        }
      }
    },

    betaLaunch: {
      status: 'Beta abierta',
      title: 'La beta ya está lista',
      subtitle: 'Empieza a usar Invextia hoy: bolsillos inteligentes, automatización y recomendaciones con IA.',
      cta: 'Entrar a la beta',
      demo: 'Ver demo',
      features: {
        pockets: { title: 'Bolsillos inteligentes', desc: 'Separa tu dinero por metas y categorías para mantener el control.' },
        ai: { title: 'Recomendaciones con IA', desc: 'Consejos claros para ahorrar, invertir y gastar mejor.' },
        automation: { title: 'Automatización', desc: 'Distribución automática de ingresos y recargas con reglas simples.' }
      },
      stats: {
        users: 'Usuarios en beta',
        satisfaction: 'Satisfacción',
        managed: 'Dinero gestionado'
      }
    },

    modal: { close: 'Cerrar' },
    footer: { explore: 'Explorar', rights: 'Todos los derechos reservados.' }
  },

  en: {
    nav: {
      home: "Home",
      about: "About",
      dashboard: "Simulation",     // Also can be "Dashboard" or "Panel"
      skills: "Product",        // Si prefieres "Skills", cámbialo aquí y en el diseño
      pricing: "Pricing",  // o "Subscription" si es un plan/boletín
      faq: "FAQ",
      contact: "Beta"
    },

    hero: {
      badge: 'Your financial copilot',
      title: 'Save, invest, pay debt, and spend in balance.',
      subtitle: 'Organize money into smart pockets and make better decisions with less effort.',
      cta: 'JOIN BETA',
      demo: 'Watch demo',
      cards: {
        analytics: 'Financial analytics',
        savings: 'Smart saving',
        investment: 'Simple investing'
      },
      scroll: 'Scroll to learn more',
      stats: {
        users: 'Users',
        satisfaction: 'Satisfaction',
        managed: 'Assets managed'
      }
    },

    about: {
      title: 'About Us',
      subtitle: 'LATAM’s financial copilot',
      byline: 'Invextia · Bogotá, Colombia',
      body1:
        'At <strong>Invextia</strong> we’re building a new way to manage personal finances in Latin America. Our <strong>AI-powered financial copilot</strong> analyzes your income, spending, debt and goals to <em>allocate your money intelligently</em>.',
      body2:
        'It works in three simple steps: connect your accounts, set goals, and our AI engine <strong>creates and executes an investment, savings and debt plan</strong> with clear explanations and real-time tracking.'
    },

    dashboard: {
      title: 'Simulate Your Financial Distribution',
      subtitle: 'Discover how Invextia would optimize your money',
      form_title: 'Customize your plan',
      income_label: 'Monthly income',
      income_placeholder: '3,500,000',
      annual: 'Annual',
      goal_label: 'Financial goal',
      goal_placeholder: 'Select your goal',
      risk_label: 'Risk profile',
      risk_placeholder: 'Select your profile',
      total_monthly: 'Monthly total',
      a11y_chart_label: 'Pie chart of financial distribution',
      goals: {
        retirement: 'Retirement',
        house: 'Buy house',
        education: 'Education',
        business: 'Own business',
        travel: 'Travel',
        emergency: 'Emergency fund',
        wealth: 'Wealth building'
      },
      risk_profiles: {
        conservative: 'Conservative',
        moderate: 'Moderate',
        aggressive: 'Aggressive'
      },
      categories: {
        emergency: 'Emergency fund',
        savings: 'Savings',
        expenses: 'Expenses',
        debts: 'Debts',
        investments: 'Investments'
      },
      descriptions: {
        emergency: 'Protection against unforeseen events',
        savings: 'Reach your future goals',
        expenses: 'Day-to-day spending',
        debts: 'Reduce your financial burden',
        investments: 'Grow your wealth'
      },
      distribution: 'Recommended distribution',
      insights: 'Personalized recommendations',
      form_description: 'Complete your financial information',
      total_distribution: 'Total distribution',
      active_categories: 'Active categories',
      reset_button: 'Reset',
      risk_descriptions: {
        conservative: 'Low risk',
        moderate: 'Balanced risk',
        aggressive: 'High performance'
      },
      insights_list: {
        raise_emergency: '⚠️ Consider increasing your emergency fund for more safety.',
        diversify: '📈 Great investment focus — diversify to reduce risk.',
        pay_high_interest: '💳 Prioritize paying high-interest debt.',
        set_specific_goals: '🎯 Strong saving discipline — set specific goals.'
      },
      tips: {
        t1: 'Automate savings to reach goals faster.',
        t2: 'Review your distribution every 3 months and adjust as needed.',
        t3: 'An emergency fund should cover 3–6 months of expenses.',
        t4: 'Invest in your financial education for better decisions.'
      },
      cta_text: 'If you want to get a more detailed distribution plan try our beta version today',
      cta_button: 'Try Beta Free'
    },

    product: {
      title: 'Product',
      how: 'How it works',
      steps: [
        'Connect your accounts (Belvo)',
        'We analyze income & expenses',
        'Define goals & risk profile',
        'Automatic distribution plan',
        'Investment execution (Alpaca)',
        'Learns from your adjustments'
      ],
      stackTitle: 'Tech stack',
      stack: [
        'Backend: FastAPI (Python)',
        'Frontend: Flutter & Web',
        'AI: LangChain + OpenAI + RAG',
        'Financial data: Belvo',
        'Broker: Alpaca',
        'DB: PostgreSQL (AWS)'
      ]
    },

    cases: { title: 'Frequently Asked Questions', details: 'See details' },

    contact: {
      title: 'Join the waitlist',
      early: 'Early access',
      early_desc: 'Be among the first to try Invextia and get launch updates.',
      trans: 'Transparency',
      trans_desc: 'We only email important updates. Unsubscribe anytime.',
      name: 'Name',
      email: 'Email',
      consent: 'I agree to receive launch/beta emails.',
      submit: 'Join the list',
      submitting: 'Joining…',
      success: 'Done! We’ll notify you when the beta opens.',
      error: 'Could not submit. Please try again.'
    },

    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about Invextia',
      items: [
        { q: 'What is Invextia?', a: 'Invextia is a fintech app that helps you organize money in smart pockets, save toward goals, invest simply, and control your finances in one place.' },
        { q: 'Is Invextia secure?', a: 'Yes. Security is our priority. We use bank-grade encryption and advanced protection protocols to keep your data and money safe.' },
        { q: 'How do “pockets” work?', a: 'Split your money into digital pockets for your goals—saving, investing, spending, or any category you define—for clarity and control.' },
        { q: 'Is Invextia a bank?', a: 'We are not a bank. Invextia is a tech platform to manage money intelligently, partnering with regulated financial institutions.' },
        { q: 'What do I need to sign up?', a: 'Just your email and a valid ID. The process is fast and 100% digital.' },
        { q: 'Where is Invextia available?', a: 'We are focused on Latin America, starting with Colombia. More countries coming soon.' },
        { q: 'Does Invextia cost money?', a: 'Basic features are free. Some premium services—like advanced investing options—may have a fee.' },
        { q: 'How do I join the waitlist?', a: 'Click “Join the Waitlist” and leave your email. We’ll notify you as soon as Invextia is ready for you.' }
      ]
    },

    pricing: {
      badge: 'Plans & Pricing',
      title: 'Choose the plan that fits you',
      subtitle: 'Start simple and upgrade as you need more features.',
      monthly: 'Monthly',
      annual: 'Annual',
      popular: 'Most popular',
      month: 'month',
      year: 'year',
      save: 'Save',
      upto: 'Up to',
      maxReturn: 'Max return',
      included: 'Includes',
      exclusive: 'Exclusive',
      ctaPrimary: 'Coming soon',
      ctaSecondary: 'Coming soon',
      guarantee:
        'No commitment. Cancel anytime. Your data is protected with bank-grade encryption.',
      tiers: {
        basic: {
          name: 'Basic',
          tagline: 'To start organizing your money',
          features: {
            f1: 'Automatic distribution engine (smart pockets).',
            f2: 'Top-ups to your pockets.',
            f3: '4% cashback on top-ups, up to COP 200,000 → up to COP 8,000 returned per month.',
            f4: 'Access to basic spending reports.'
          }
        },
        advanced: {
          name: 'Advanced',
          tagline: 'More control and AI recommendations',
          features: {
            f1: 'Everything in Basic.',
            f2: 'AI chatbot for simple financial suggestions (saving, spending control).',
            f3: 'Detailed reports on spending and saving habits.',
            f4: '6% cashback on top-ups, up to COP 500,000 → up to COP 30,000 returned per month.'
          },
          exclusive: {
            e1: 'Personalized saving recommendations.'
          }
        },
        premium: {
          name: 'Premium',
          tagline: 'The full Invextia experience',
          features: {
            f1: 'Everything in Advanced.',
            f2: 'Advanced AI chatbot (projections, investment goals, personalized guidance).',
            f3: 'Early access to new features (bank integrations, global investing via Alpaca).',
            f4: '8% cashback on top-ups, up to COP 1,000,000 → up to COP 80,000 returned per month.'
          },
          exclusive: {
            e1: 'Premium invites for friends.',
            e2: 'Priority support.',
            e3: 'Exclusive benefits and special promotions.'
          }
        }
      }
    },

    betaLaunch: {
      status: 'Open beta',
      title: 'Beta is live',
      subtitle: 'Start using Invextia today: smart pockets, automation, and AI-powered recommendations.',
      cta: 'Open app',
      demo: 'Watch demo',
      features: {
        pockets: { title: 'Smart pockets', desc: 'Split money by goals and categories to stay in control.' },
        ai: { title: 'AI recommendations', desc: 'Clear suggestions to save, invest, and spend better.' },
        automation: { title: 'Automation', desc: 'Automatic income distribution and top-ups with simple rules.' }
      },
      stats: {
        users: 'Beta users',
        satisfaction: 'Satisfaction',
        managed: 'Assets managed'
      }
    },

    modal: { close: 'Close' },
    footer: { explore: 'Explore', rights: 'All rights reserved.' }
  }
};

function detectLocale() {
  const saved = localStorage.getItem('locale');
  if (saved) return saved;
  const lang = (navigator.language || 'en').toLowerCase();
  return lang.startsWith('es') ? 'es' : 'en';
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  fallbackLocale: 'en',
  messages
});

export function setLocale(locale) {
  i18n.global.locale.value = locale;
  localStorage.setItem('locale', locale);
}
